import { jsPDF } from 'jspdf'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import type { ProvisionSummary, ProvisionReportData } from './provisions'

// Load image as base64
async function loadImageAsBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } else {
        reject(new Error('Could not get canvas context'))
      }
    }
    img.onerror = () => reject(new Error('Could not load image'))
    img.src = url
  })
}

// Generate provision report PDF
export async function generateProvisionPDF(
  summary: ProvisionSummary,
  dateFrom: Date,
  dateTo: Date
): Promise<void> {
  const doc = new jsPDF()

  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 15
  const contentWidth = pageWidth - 2 * margin
  let yPos = margin

  // Try to load and add logo (using provision-specific logo)
  try {
    const logoBase64 = await loadImageAsBase64('/logo-provision.png')
    // Logo is square, use 28x28 for better visibility
    doc.addImage(logoBase64, 'PNG', margin, yPos - 2, 28, 28)
  } catch (error) {
    console.warn('Could not load logo:', error)
  }

  // Header text - offset to the right of logo
  const textStartX = margin + 30

  // Format period as "januari 2026" for display
  const periodMonth = format(dateFrom, 'MMMM yyyy', { locale: sv })
  // Reference ID for invoice: "PROV-2026-01"
  const periodRef = `PROV-${format(dateFrom, 'yyyy-MM')}`

  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('MB Trädgård Support AB', textStartX, yPos + 8)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text(`Provisionsunderlag ${periodMonth}`, textStartX, yPos + 17)

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(summary.partner_name, textStartX, yPos + 24)

  doc.setFontSize(9)
  doc.setTextColor(100)
  doc.text(`Underlagsnr: ${periodRef}`, textStartX, yPos + 30)
  doc.text(`Period: ${format(dateFrom, 'yyyy-MM-dd')} - ${format(dateTo, 'yyyy-MM-dd')}`, textStartX + 60, yPos + 30)

  yPos += 40

  // Info box
  doc.setTextColor(0)
  doc.setFontSize(9)
  doc.text(`Provisionsprocent: ${summary.partner_percentage}%`, margin, yPos)
  doc.text(`Antal kunder: ${summary.customer_count}`, margin + 50, yPos)
  doc.text(`Antal rader: ${summary.line_items.length}`, margin + 90, yPos)
  yPos += 10

  // Table - adjusted column widths for A4 (with space for "kr" suffix)
  // Updr: 12, Date: 18, Customer+Address: 46, Article: 30, Qty: 10, Unit: 12, Price: 26, Total: 26 = 180
  const colWidths = [12, 18, 46, 30, 10, 12, 26, 26]
  const colHeaders = ['Updr.', 'Datum', 'Kund / Adress', 'Artikel', 'Ant.', 'Enhet', 'À-pris', 'Summa']
  const startX = margin

  // Draw table header
  doc.setFillColor(245, 245, 245)
  doc.rect(startX, yPos - 4, contentWidth, 8, 'F')

  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')

  let xPos = startX
  for (let i = 0; i < colHeaders.length; i++) {
    const align = i >= 6 ? 'right' : 'left'  // À-pris, Summa are right-aligned
    const textX = align === 'right' ? xPos + colWidths[i] - 2 : xPos + 2
    doc.text(colHeaders[i], textX, yPos, { align: align === 'right' ? 'right' : 'left' })
    xPos += colWidths[i]
  }

  yPos += 5

  // Draw line
  doc.setDrawColor(200)
  doc.line(startX, yPos, startX + contentWidth, yPos)
  yPos += 4

  // Table rows
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)

  for (const item of summary.line_items) {
    // Check if we need a new page (need space for 2 lines per row)
    if (yPos > 270) {
      doc.addPage()
      yPos = margin

      // Redraw header on new page
      doc.setFillColor(245, 245, 245)
      doc.rect(startX, yPos - 4, contentWidth, 8, 'F')
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      xPos = startX
      for (let i = 0; i < colHeaders.length; i++) {
        const align = i >= 6 ? 'right' : 'left'  // À-pris, Summa are right-aligned
        const textX = align === 'right' ? xPos + colWidths[i] - 2 : xPos + 2
        doc.text(colHeaders[i], textX, yPos, { align: align === 'right' ? 'right' : 'left' })
        xPos += colWidths[i]
      }
      yPos += 5
      doc.setDrawColor(200)
      doc.line(startX, yPos, startX + contentWidth, yPos)
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
    }

    xPos = startX

    // Assignment number
    const assignmentNum = item.assignment_number ? `#${item.assignment_number}` : '-'
    doc.text(assignmentNum, xPos + 2, yPos)
    xPos += colWidths[0]

    // Date
    doc.text(item.start_date, xPos + 2, yPos)
    xPos += colWidths[1]

    // Customer name (bold) on first line
    doc.setFont('helvetica', 'bold')
    const customerName = truncateText(doc, item.customer_name, colWidths[2] - 4)
    doc.text(customerName, xPos + 2, yPos)
    const customerColX = xPos

    // Article
    doc.setFont('helvetica', 'normal')
    const articleColX = xPos + colWidths[2]
    const articleName = truncateText(doc, item.article_name, colWidths[3] - 4)
    doc.text(articleName, articleColX + 2, yPos)

    // Quantity
    const qtyColX = articleColX + colWidths[3]
    doc.text(item.quantity.toString(), qtyColX + 2, yPos)

    // Unit
    const unitColX = qtyColX + colWidths[4]
    const unitText = truncateText(doc, item.unit || 'st', colWidths[5] - 4)
    doc.text(unitText, unitColX + 2, yPos)

    // Unit price (right aligned, with kr)
    const priceColX = unitColX + colWidths[5]
    doc.text(formatCurrency(item.unit_price), priceColX + colWidths[6] - 2, yPos, { align: 'right' })

    // Total (right aligned, with kr)
    const totalColX = priceColX + colWidths[6]
    doc.text(formatCurrency(item.total_price), totalColX + colWidths[7] - 2, yPos, { align: 'right' })

    yPos += 4

    // Customer address on second line (smaller, gray)
    doc.setTextColor(100)
    doc.setFontSize(6)
    const customerAddress = truncateText(doc, item.customer_address, colWidths[2] - 4)
    doc.text(customerAddress, customerColX + 2, yPos)
    doc.setTextColor(0)
    doc.setFontSize(7)

    yPos += 5
  }

  // Summary section
  yPos += 4
  doc.setDrawColor(200)
  doc.line(startX, yPos, startX + contentWidth, yPos)
  yPos += 8

  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')

  // Total provision base
  doc.text('Summa provisionsgrundande (exkl. moms):', margin, yPos)
  doc.text(formatCurrency(summary.total_provision_base), pageWidth - margin, yPos, { align: 'right' })
  yPos += 7

  // Provision amount (highlighted)
  doc.setFillColor(255, 251, 235) // amber-50
  doc.rect(margin - 2, yPos - 4, contentWidth + 4, 10, 'F')

  doc.text(`Provision (${summary.partner_percentage}%):`, margin, yPos)
  doc.setTextColor(180, 83, 9) // amber-700
  doc.text(formatCurrency(summary.total_provision), pageWidth - margin, yPos, { align: 'right' })
  doc.setTextColor(0)

  yPos += 16

  // Footer
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100)
  doc.text(`Underlagsnr: ${periodRef}  •  Genererat: ${format(new Date(), 'yyyy-MM-dd HH:mm', { locale: sv })}`, margin, yPos)

  // Save the PDF - include reference in filename
  const filename = `${periodRef}_${summary.partner_name.toLowerCase().replace(/\s+/g, '_')}.pdf`
  doc.save(filename)
}

// Helper function to truncate text to fit within a width
function truncateText(doc: jsPDF, text: string, maxWidth: number): string {
  if (!text) return ''
  const textWidth = doc.getTextWidth(text)
  if (textWidth <= maxWidth) {
    return text
  }

  let truncated = text
  while (doc.getTextWidth(truncated + '...') > maxWidth && truncated.length > 0) {
    truncated = truncated.slice(0, -1)
  }
  return truncated + '...'
}

// Format currency with Swedish locale and kr suffix
function formatCurrency(num: number): string {
  return num.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr'
}

// Generate provision report PDF from stored report data
export async function generateProvisionPDFFromData(reportData: ProvisionReportData): Promise<void> {
  // Convert stored data to ProvisionSummary format
  const summary: ProvisionSummary = {
    partner_id: '',
    partner_name: reportData.partner_name,
    partner_email: null,
    partner_percentage: reportData.partner_percentage,
    total_sales: reportData.total_sales,
    total_provision_base: reportData.total_provision_base,
    total_provision: reportData.total_provision,
    line_items: reportData.line_items,
    customer_count: reportData.customer_count,
    paid_periods: []
  }

  const dateFrom = new Date(reportData.date_from)
  const dateTo = new Date(reportData.date_to)

  // Use the existing function to generate the PDF
  await generateProvisionPDF(summary, dateFrom, dateTo)
}
